// web/src/lib/status.ts — single source: RunStatus → label/tone/icon (D04 §status, D05). Status is NEVER
// color-only (icon + label too). Also money + relative-age formatters working in CONTRACT units
// (integer cents, unix seconds).
import type { RunStatus } from './types';

export type Tone = 'muted' | 'info' | 'warn' | 'run' | 'success' | 'danger';

// Non-techy voice (CHALICE_TASKS_SPEC §8, 22_PREVIEW_AND_PUBLISH.md §3): the customer sees plain
// language, never pipeline jargon. THIS MAP IS THE ONLY PLACE A STATUS IS NAMED FOR A HUMAN.
//
// It is exhaustive over RunStatus by type, which is the point: the API emitted `awaiting_credits`
// (doc 11), `verifying` and `needs_attention` (doc 20) for months while this file did not know
// they existed, so those requests rendered with no status at all (90_OPEN_ITEMS D-4 + D-12).
// Adding a status to the union now forces a label to be written here. Do not widen the type to
// `string` to make a new status compile — write the copy.
//
// No status name, gate check name, or branch name may appear in a label (§3).
export const STATUS_UI: Record<RunStatus, { label: string; tone: Tone; icon: string }> = {
  queued: { label: 'In the queue', tone: 'muted', icon: 'clock' },
  working: { label: 'Working on it', tone: 'info', icon: 'loader' },
  clarifying: { label: 'We have a question', tone: 'warn', icon: 'help' },
  rejected: { label: "Can't make this change", tone: 'muted', icon: 'x' },
  // D-12: the QA gate leg. `verifying` is our checks running, and it is deliberately worded as
  // work in progress rather than as a test the customer's update might fail.
  verifying: { label: 'Checking your update', tone: 'info', icon: 'search' },
  preview_ready: { label: 'Ready to review', tone: 'run', icon: 'image' },
  // §3 gives this "Publishing — about 3 minutes". The minutes live in PublishProgress, which
  // counts against that site's real median; a badge cannot know the number, and a badge that
  // guesses is exactly the lie §2 is trying to remove.
  merging: { label: 'Publishing', tone: 'info', icon: 'rocket' },
  live: { label: 'Live on your site', tone: 'success', icon: 'check' },
  // D-12: gate red twice — a HOLD an admin owns, not a failure and not the customer's problem.
  // Says nothing about checks, counts, or what went wrong (doc 20 §7 item 9).
  needs_attention: { label: "We're taking a closer look", tone: 'warn', icon: 'shield' },
  // D-4: the doc 11 / 02 §5 soft stop. Credits, never dollars (doc 02 §2).
  awaiting_credits: { label: 'Waiting for credits', tone: 'warn', icon: 'wallet' },
  // Was "Needs attention", which now collides with the status of that name. §3's copy is better
  // anyway: it is about the update, and it does not imply the customer must do something.
  failed: { label: 'Something went wrong', tone: 'danger', icon: 'alert' },
  archived: { label: 'Archived', tone: 'muted', icon: 'box' },
  // Portal spec 19 — the update agent's turns. Plain words; none of them is a problem the customer must solve.
  waiting: { label: 'Up next', tone: 'muted', icon: 'clock' },
  set_aside: { label: 'Set aside', tone: 'muted', icon: 'box' },
  queued_for_update: { label: 'Added next month', tone: 'info', icon: 'image' },
  publish_waiting: { label: 'Going live next', tone: 'info', icon: 'rocket' },
};

export const TONE_TEXT: Record<Tone, string> = {
  muted: 'text-muted', info: 'text-info', warn: 'text-warn', run: 'text-run', success: 'text-success', danger: 'text-danger',
};
export const TONE_BORDER: Record<Tone, string> = {
  muted: 'border-hair', info: 'border-info', warn: 'border-warn', run: 'border-run', success: 'border-success', danger: 'border-danger',
};

// Fixed "now" so relative ages are stable in mock mode.
const NOW_S = Math.floor(Date.parse('2026-06-26T18:00:00Z') / 1000);

// Relative age from a unix-seconds timestamp (e.g. "3h", "2d").
export function age(seconds: number, nowS = NOW_S): string {
  const diff = Math.max(0, nowS - seconds);
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// Integer cents → "$1,234.56". Optional sign for income/payout context.
export function money(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// Unix seconds → locale date.
export function dateOf(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString();
}
